import {Select} from 'antd';
import {FormInstance} from 'antd/es/form';
import {SelectProps} from 'antd/es/select';
import {PropertyPath, groupBy, set} from 'lodash-es';
import React, {
    CSSProperties, Key, ReactElement, Ref, forwardRef,
    useEffect, useImperativeHandle, useMemo, useRef,
} from 'react';

import {IFormItemProps} from 'shared/types';
import { ERequestType } from 'shared/types/requests';
import {getPopupContainer, setFieldsValueUntouched} from 'shared/utils';

import {TagRenderer} from '../reference-select/tag-renderer';
import {CustomSelectSpinner} from './components/custom-select-spinner';
import {SelectSuffixIcon} from './custom-select-suffix';
import {createFieldFormatterForSelect, createSortComparator, TLookupValueKey, TSortComparator, TSortDirection} from './custom-select-utils';
import {useCustomSelectConfig} from './hooks/use-custom-select-config';
import {useCustomSelectReferenceOptionsFetcher} from './hooks/use-custom-select-reference-options-fetcher';

import './custom-select.less';

export interface ICustomSelectEntry<L, V> {/*  */
    label: L;
    value: V;
}

export enum ECustomSelectMode {
    multiple = 'multiple',
}

interface ICustomSelectSettings {
    formInstance?: FormInstance;
    formFieldKey?: Key;
    formFieldPathName?: PropertyPath;

    isDisabled?: boolean;
    isLoading?: boolean;
    isClearable?: boolean;

    showNoValueWhenNoOptions?: boolean;

    referenceUrlQueryParams?: { [index: string]: string };

    url?: string;
    placeholder?: string;
    labelPath?: string;
    valuePath?: string;

    useFirstOptionAfterReceivedFromFetcher?: boolean;
    useFirstOptionAfterEntriesChanged?: boolean;
    useUndefinedAfterEntriesChanged?: boolean;
    noUseFirstOptionWhenValueIsSet?: boolean;
    setFirstOptionUntouched?: boolean; /* Если true, то форма НЕ станет touched (form.isFieldsTouched())
        после автоматической установки (useFirstOptionAfterReceivedFromFetcher) первого значения в селекте.
        Для работы нужно передать formInstance и formFieldKey.
        Если используется в Form.List, то нужно дополнительно передать formFieldPathName.
    */

    depsToCallCustomTrigger?: any[];

    fetchTrigger?: number | string | boolean;

    showSearch?: boolean;

    isReferenceNonStorable?: boolean;

    triggerOnValueSet?: boolean;
    allowTriggerWhenNoValueFound?: boolean; /* Разрешить триггеру срабатывать, если объект по ключу-значению в
    списке options (entries) не найден.
    Используется, если нужно передать остальные параметры (entries и т.д.), но value не определён. */

    showNullValueAsUndefined?: boolean;
    showEmptyStringAsUndefined?: boolean;
    setNullInsteadOfUndefinedOnChange?: boolean;

    mode?: ECustomSelectMode;
    size?: SelectProps<any>['size'];

    requestType?: ERequestType;
}

export enum ECustomSelectValueTriggerEvent {
    valueWasSet = 'valueWasSet',
    valueHasBeenChangedManually = 'valueHasBeenChangedManually',
    firstOptionWasSet = 'firstOptionWasSet',
    receivedDataFromFetcher = 'receivedDataFromFetcher',
    customTrigger = 'customTrigger',
}

interface IOptionsFilterCallbackArgs<L, V> {
    entry: ICustomSelectEntry<L, V>;
    entriesToRender: ICustomSelectEntry<L, V>[];
}

export interface ICustomSelectProps<L, V> extends IFormItemProps {
    entries?: ICustomSelectEntry<L, V>[];
    additionalEntries?: ICustomSelectEntry<L, V>[];
    // Используются для определения label, когда выбранного entry больше нет в entries
    entriesForLabels?: ICustomSelectEntry<L, V>[];
    // Используется только для определения label
    initialValue?: V;
    settings?: ICustomSelectSettings;
    style?: CSSProperties;
    dropdownClassName?: string;
    handleTriggerEvent?: (
        value?: any,
        event?: ECustomSelectValueTriggerEvent,
        entries?: ICustomSelectEntry<L, V>[]
    ) => void;
    optionsFilterCallback?: (args: IOptionsFilterCallbackArgs<L, V>) => boolean;
    optionsLabelFormatter?: (label: L, value: V) => any;
    rawDataInStoreFormatter?: (data: any) => any;
    setShouldRefetchData?: (value: boolean) => void;
    fixedDropdown?: boolean;
    sort?: {
      order: TSortDirection;
      sortedByValue: TLookupValueKey<V>;
    };
}

export interface ICustomSelectComponentRef {
    entriesFromFetcher?: { label: any; value: any}[];
}

export const CustomSelectComponent = <L, V>({
    entries = [],
    additionalEntries,
    entriesForLabels,
    initialValue,
    value: formFieldValue,
    onChange: onSelectValueChange,
    settings = {},
    style,
    handleTriggerEvent,
    optionsFilterCallback,
    rawDataInStoreFormatter,
    optionsLabelFormatter,
    setShouldRefetchData,
    dropdownClassName,
    fixedDropdown = true,
    sort,
}: ICustomSelectProps<L, V>, ref: Ref<ICustomSelectComponentRef>) => {
    const {
        formFieldKey,
        formInstance,
        formFieldPathName,
        isDisabled,
        isLoading,
        isClearable,
        url,
        referenceUrlQueryParams,
        placeholder,
        labelPath,
        valuePath,
        useFirstOptionAfterReceivedFromFetcher,
        useFirstOptionAfterEntriesChanged,
        useUndefinedAfterEntriesChanged,
        noUseFirstOptionWhenValueIsSet,
        showNoValueWhenNoOptions,
        depsToCallCustomTrigger = [],
        showSearch,
        isReferenceNonStorable,
        triggerOnValueSet,
        setFirstOptionUntouched,
        mode,
        allowTriggerWhenNoValueFound,
        showNullValueAsUndefined,
        setNullInsteadOfUndefinedOnChange,
        showEmptyStringAsUndefined,
        size,
        fetchTrigger,
        requestType,
    } = settings;

    if (url && (!valuePath || !labelPath)) throw new Error('Не указан labelPath или valuePath.');

    const valueWasSetRef = useRef<boolean>(false);

    const referenceUrl = (() => {
        const querySuffix = new URLSearchParams(referenceUrlQueryParams).toString();
        return querySuffix ? `${url}?${querySuffix}` : url;
    })();

    const {isFetchingEntries, entriesFromFetcher} = useCustomSelectReferenceOptionsFetcher({
        referenceUrl,
        labelPath,
        referenceUrlQueryParams,
        isReferenceNonStorable,
        additionalEntries,
        rawDataInStoreFormatter,
        fetchTrigger,
        requestType,
        setShouldRefetchData,
    });

    const entriesToRender = useMemo(() => {
        if (entriesFromFetcher) return entriesFromFetcher;
        return entries;
    }, [entries, entriesFromFetcher]);
    const pathToEntriesArrayMapping = groupBy(entriesToRender.map(entry => entry.value), valuePath);

    const formatFieldValueForSelect = createFieldFormatterForSelect(valuePath);
    const selectedCurrentFieldValueFromForm = formatFieldValueForSelect(formFieldValue);
    const sortComparator: TSortComparator<L, V> | undefined = sort && createSortComparator<L, V>(sort.order, sort.sortedByValue);

    const {
        filterEntry,
        filteredEntries,
        dropdownOpen,
        setDropdownOpen,
        setDropdownSelectedIcon,
        multipleModeDropdownWithSearch,
    } = useCustomSelectConfig({
        mode,
        showSearch,
        entriesToRender,
        optionsFilterCallback,
        formatFieldValueForSelect,
        optionsLabelFormatter,
        sortComparator
    });

    const {entriesFilteredJSX, entriesFilteredRaw} = filteredEntries;

    const setFirstValueInEntriesAsCurrentFieldValue = (entriesToPickDataFrom?: ICustomSelectEntry<L, V>[]) => {
        if (!entriesToPickDataFrom) return;

        if (formInstance && formFieldKey) {
            if (entriesToPickDataFrom?.length) {
                const [{value: firstEntryValue}] = entriesToPickDataFrom;
                const firstValueFormatted = formatFieldValueForSelect(firstEntryValue);
                if (setFirstOptionUntouched) {
                    if (formFieldPathName) {
                        const fieldList = formInstance.getFieldValue(formFieldKey);
                        set(fieldList, formFieldPathName, firstValueFormatted);
                        if (true as any) return;
                        setFieldsValueUntouched(formInstance, {[formFieldKey]: fieldList});
                    } else {
                        setFieldsValueUntouched(formInstance, {[formFieldKey]: firstValueFormatted});
                    }
                } else formInstance.setFieldsValue({[formFieldKey]: firstValueFormatted});
                handleTriggerEvent?.(firstEntryValue, ECustomSelectValueTriggerEvent.firstOptionWasSet);
            } else {
                if (setFirstOptionUntouched) {
                    setFieldsValueUntouched(formInstance, {[formFieldKey]: undefined});
                } else { formInstance.setFieldsValue({[formFieldKey]: undefined}); }
                handleTriggerEvent?.(undefined, ECustomSelectValueTriggerEvent.firstOptionWasSet);
            }
        } else if (entriesToPickDataFrom.length) {
            const [{value: firstEntryValue}] = entriesToPickDataFrom;
            const firstValueFormatted = formatFieldValueForSelect(firstEntryValue);
            onSelectValueChange?.(firstValueFormatted, [{touched: false}]);
            handleTriggerEvent?.(firstEntryValue, ECustomSelectValueTriggerEvent.firstOptionWasSet);
        }
    };

    const useFirstOption = useFirstOptionAfterReceivedFromFetcher;

    const callTrigger = (event = ECustomSelectValueTriggerEvent.customTrigger) => {
        const currentValue = (() => {
            if (pathToEntriesArrayMapping?.[formFieldValue]?.length) {
                const [value] = pathToEntriesArrayMapping[formFieldValue];
                if (!allowTriggerWhenNoValueFound) {
                    handleTriggerEvent?.(value, event, entriesFromFetcher);
                }
                return value;
            }
            return formFieldValue;
        })();

        if (allowTriggerWhenNoValueFound) {
            handleTriggerEvent?.(currentValue, event, entriesFromFetcher);
        }
    };

    useEffect(() => {
        if (depsToCallCustomTrigger.length) callTrigger();
    }, [...depsToCallCustomTrigger]);

    useEffect(() => {
        if (entriesFilteredRaw?.length) {
            callTrigger(ECustomSelectValueTriggerEvent.receivedDataFromFetcher);
            if (useFirstOption) {
                if (noUseFirstOptionWhenValueIsSet && formFieldValue) return;
                setFirstValueInEntriesAsCurrentFieldValue(entriesFilteredRaw);
            }
            if (useUndefinedAfterEntriesChanged && onSelectValueChange) {
                onSelectValueChange(undefined, {});
            }
        }
    }, [entriesFilteredRaw]);

    useEffect(() => {
        if (noUseFirstOptionWhenValueIsSet && formFieldValue) return;
        if (useFirstOptionAfterEntriesChanged) {
            setFirstValueInEntriesAsCurrentFieldValue(entries);
        }
    }, [entries]);

    useImperativeHandle(ref, () => ({
        entriesFromFetcher,
    }));

    return (
        <>
            <Select
                size={size}
                allowClear={isClearable}
                className="custom-select select-field"
                dropdownClassName={dropdownClassName}
                getPopupContainer={getPopupContainer(fixedDropdown)}
                value={(() => {
                    if (isLoading || isFetchingEntries) return <CustomSelectSpinner />;
                    if (showEmptyStringAsUndefined && selectedCurrentFieldValueFromForm === '') return undefined;
                    if (showNullValueAsUndefined && selectedCurrentFieldValueFromForm === null) return undefined;
                    if (showNoValueWhenNoOptions && !entriesToRender.length) return undefined;
                    // trigger value setting event with the object from the reference, not the value itself
                    if (triggerOnValueSet && !valueWasSetRef.current && selectedCurrentFieldValueFromForm) {
                        const refValues = pathToEntriesArrayMapping[selectedCurrentFieldValueFromForm];
                        if (refValues?.length) {
                            const [value] = refValues;
                            handleTriggerEvent?.(value, ECustomSelectValueTriggerEvent.valueWasSet);
                            valueWasSetRef.current = true;
                        }
                    }
                    if (mode === ECustomSelectMode.multiple) {
                        return formFieldValue;
                    }
                    if (entriesForLabels && initialValue) {
                        const initialLabel = entriesForLabels.find(entry => entry.value === initialValue)?.label;
                        return initialLabel || selectedCurrentFieldValueFromForm;
                    }
                    return selectedCurrentFieldValueFromForm;
                })()} // ant не умеет хранить тут объекты, нужен уникальный примитив
                onChange={(newValueFormatted, option) => {
                    const valueForTrigger = (() => { // в formatted - сырой id
                        // в valueForTrigger - полный объект для этого id
                        if (setNullInsteadOfUndefinedOnChange && newValueFormatted === undefined) {
                            return null;
                        }
                        if (!newValueFormatted || newValueFormatted?.length === 0) {
                            return newValueFormatted?.length === 0 ? undefined : newValueFormatted;
                        }
                        if (Array.isArray(newValueFormatted) && newValueFormatted.length > 1) {
                            return newValueFormatted?.map((val: any) => {
                                const [valueConverted] = pathToEntriesArrayMapping[val];
                                return valueConverted;
                            });
                        }
                        const [value] = pathToEntriesArrayMapping[newValueFormatted];
                        return value;
                    })();

                    const setNewValue = (newValue: any) => {
                        if (onSelectValueChange) onSelectValueChange(newValue, option);
                        else if (formInstance && formFieldKey) {
                            formInstance.setFieldsValue({[formFieldKey]: newValue});
                        }
                    };

                    if (setNullInsteadOfUndefinedOnChange && newValueFormatted === undefined) setNewValue(null);
                    else setNewValue(newValueFormatted);

                    handleTriggerEvent?.(valueForTrigger, ECustomSelectValueTriggerEvent.valueHasBeenChangedManually);
                }}
                placeholder={placeholder}
                open={dropdownOpen}
                style={{...style}}
                disabled={isFetchingEntries || isDisabled}
                onDropdownVisibleChange={v => {
                    setDropdownOpen(v);
                }}
                mode={mode}
                tagRender={TagRenderer}
                showSearch={showSearch}
                filterOption={filterEntry}
                menuItemSelectedIcon={mode === ECustomSelectMode.multiple && setDropdownSelectedIcon}
                dropdownRender={mode === ECustomSelectMode.multiple
                && showSearch ? multipleModeDropdownWithSearch : undefined}
                suffixIcon={(
                    <div onClick={() => {
                        setDropdownOpen(p => !p);
                    }}
                    >
                        <SelectSuffixIcon
                            showSearch={showSearch}
                            isDropdownOpen={dropdownOpen}
                            isSelectDisabled={isDisabled}
                            isClearable={isClearable}
                        />
                    </div>
                )}
            >
                {entriesFilteredJSX}
            </Select>
        </>
    );
};

export const CustomSelect = forwardRef(CustomSelectComponent) as
    <L=string, V=any>(p: ICustomSelectProps<L, V> & {ref?: Ref<ICustomSelectComponentRef>}) => ReactElement;

export const GenericCustomSelect = forwardRef(CustomSelectComponent) as
    <L, V>(p: ICustomSelectProps<L, V> & {ref?: Ref<ICustomSelectComponentRef>}) => ReactElement;
